import React, { useEffect, useState } from "react";
import { Save, X } from "lucide-react";

import { FormLayout } from "../../../../../components/layout/forms/FormLayout";
import FormHeader from "../../../../../components/layout/forms/header/FormHeader";

import Heading from "../../../../../components/typography/Heading";
import Text from "../../../../../components/typography/Text";
import Button from "../../../../../components/buttons/Button";

import TextField from "../../../../../components/input/textfield/TextField";
import TextArea from "../../../../../components/input/textarea/TextArea";
import Select from "../../../../../components/input/select/Select";

import { useCreatorVideos } from "../../hooks/useCreatorVideos";

import {
  getSetPriceAmount,
  validateVideoPriceAmount,
} from "./utils/helper/videoPricing";

import { validateRequiredFields } from "../../../../../utils/helpers/validation/validateRequiredFields";

import "./CreatorVideoEditForm.css";

const VISIBILITY_OPTIONS = [
  { label: "Public", value: "public" },
  { label: "Unlisted", value: "unlisted" },
  { label: "Private", value: "private" },
];

const SPORT_OPTIONS = [
  { label: "Select sport", value: "" },
  { label: "Rugby Union", value: "Rugby Union" },
  { label: "Rugby League", value: "Rugby League" },
  { label: "Other", value: "Other" },
];

export default function CreatorVideoEditForm({
  username,
  videoTag,
  onClose,
  onCancel,
  onSuccess,
}) {
  const {
    videoData,
    setVideoData,
    loading,
    error,
    getCreatorVideoEditData,
    updateCreatorVideo,
  } = useCreatorVideos(username);

  const [formError, setFormError] = useState("");
  const [priceTouched, setPriceTouched] = useState(false);
  const [editLoaded, setEditLoaded] = useState(false);

  const visibility = String(videoData?.visibility || "private")
    .trim()
    .toLowerCase();

  const videoDuration = videoData?.videoDuration;

  const setPriceAmount = getSetPriceAmount(videoDuration);

  const priceValidation = validateVideoPriceAmount(
    videoData?.priceAmount,
    videoDuration,
  );

  const priceError =
    editLoaded && priceTouched && !priceValidation.valid
      ? priceValidation.message
      : "";

  const formDisabled = loading || !editLoaded;

  const errorMessage =
    formError || (typeof error === "string" ? error : error?.message || "");

  useEffect(() => {
    let active = true;

    setFormError("");
    setPriceTouched(false);
    setEditLoaded(false);

    if (!username || !videoTag) return;

    const loadVideo = async () => {
      try {
        const result = await getCreatorVideoEditData(videoTag);

        if (!active) return;

        setEditLoaded(Boolean(result?.video));
      } catch (loadError) {
        if (!active) return;

        setFormError(loadError?.message || "Unable to load video details.");
      }
    };

    void loadVideo();

    return () => {
      active = false;
    };
  }, [username, videoTag]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setVideoData((previous) => ({
      ...previous,
      [name]: value,
    }));

    setFormError("");
  };

  const handlePriceChange = (event) => {
    const value = event.target.value;

    if (value !== "" && !/^\d*(?:\.\d{0,2})?$/.test(value)) {
      return;
    }

    setVideoData((previous) => ({
      ...previous,
      priceAmount: value,
    }));

    setPriceTouched(true);
    setFormError("");
  };

  const handleClose = () => {
    if (loading) return;

    if (typeof onClose === "function") {
      onClose();
      return;
    }

    onCancel?.(false);
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();

    if (formDisabled || !videoData) return;

    setFormError("");

    const requiredResult = validateRequiredFields(videoData, [
      "title",
      "sport",
      "visibility",
      "priceAmount",
    ]);

    if (!requiredResult.isValid) {
      setFormError("Please fill in all required fields.");
      return;
    }

    const validation = validateVideoPriceAmount(
      videoData.priceAmount,
      videoData.videoDuration,
    );

    if (!validation.valid) {
      setPriceTouched(true);
      setFormError(validation.message);
      return;
    }

    const payload = {
      title: String(videoData.title || "").trim(),
      sport: String(videoData.sport || "").trim(),
      description: String(videoData.description || "").trim(),
      visibility: String(videoData.visibility || "private").toLowerCase(),
      priceAmount: validation.priceAmount,
    };

    try {
      const result = await updateCreatorVideo(videoTag, payload);

      if (result?.success) {
        await onSuccess?.(result);
        handleClose();
      } else {
        setFormError(result?.message || "Unable to update video details.");
      }
    } catch (error) {
      console.error("Video update failed:", {
        status: error?.response?.status,
        response: error?.response?.data,
        payload,
      });

      setFormError(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to update video details.",
      );
    }
  };

  return (
    <FormLayout className="creator-video-edit-form">
      <FormHeader
        title="Edit Video"
        description="Update your video details, visibility and rental price."
      />

      {errorMessage && (
        <div className="creator-video-edit-form__error" role="alert">
          <Text as="p" size="sm">
            {errorMessage}
          </Text>
        </div>
      )}

      <div className="creator-video-edit-form__fields">
        <TextField
          label="Title"
          name="title"
          value={videoData?.title || ""}
          onChange={handleInputChange}
          required
          disabled={formDisabled}
          placeholder="Match name or highlight title"
        />

        <div className="creator-video-edit-form__row">
          <Select
            label="Sport"
            name="sport"
            value={videoData?.sport || ""}
            onChange={handleInputChange}
            items={SPORT_OPTIONS}
            required
            disabled={formDisabled}
          />

          <Select
            label="Visibility"
            name="visibility"
            value={visibility}
            onChange={handleInputChange}
            items={VISIBILITY_OPTIONS}
            required
            disabled={formDisabled}
          />
        </div>

        <TextArea
          label="Description"
          name="description"
          value={videoData?.description || ""}
          onChange={handleInputChange}
          maxLength={500}
          maxHeight={100}
          disabled={formDisabled}
          placeholder="Add context for viewers, players or scouts"
        />

        <div className="creator-video-edit-form__pricing">
          <Heading as="h3" size="sm">
            Rental Pricing
          </Heading>

          <Text as="p" size="sm" tone="secondary">
            Set the rental price for viewers accessing this video.
          </Text>

          <TextField
            label="Rental Price (AUD)"
            name="priceAmount"
            type="number"
            inputType="number"
            min={setPriceAmount ?? undefined}
            step="0.01"
            inputMode="decimal"
            value={videoData?.priceAmount ?? ""}
            onChange={handlePriceChange}
            onBlur={() => setPriceTouched(true)}
            disabled={formDisabled}
            required
            placeholder={
              setPriceAmount === null
                ? "Minimum price unavailable"
                : `Minimum A$${setPriceAmount.toFixed(2)}`
            }
          />

          <Text as="p" size="xs" tone="secondary">
            {setPriceAmount === null
              ? "The video's duration is required to calculate its minimum rental price."
              : `Minimum rental price: A$${setPriceAmount.toFixed(2)}. You can set a higher price.`}
          </Text>

          {priceError && (
            <Text
              as="p"
              size="xs"
              className="creator-video-edit-form__price-error"
            >
              {priceError}
            </Text>
          )}
        </div>
      </div>

      <div style={{ display: "flex", marginTop: "1.5rem" }}>
        <Button
          value="Cancel"
          variant="primary-outlined"
          icon={<X size={16} />}
          onClick={handleClose}
          disabled={loading}
          style={{ width: "100%" }}
        />

        <Button
          value={
            loading ? (editLoaded ? "Saving..." : "Loading...") : "Save Changes"
          }
          variant="primary"
          icon={<Save size={16} />}
          onClick={handleSubmit}
          disabled={formDisabled}
          style={{ width: "100%" }}
        />
      </div>
    </FormLayout>
  );
}
